import { useEffect, useState } from 'react';
import { API_ORIGIN } from '../config';

/*
 * The current direct order approvers, from the backend (GET /api/direct-orders/approvers):
 * [{ level: 1|2|3, key: 'first'|'second'|'third', label, email, name, title }].
 * Admins change them on the Approvers page, so nothing here is hard-coded. Loaded once per session
 * and shared; call refreshApprovers() after a change.
 */
let chain = [];
let loaded = false;
let pending = null;
const listeners = new Set();

const notify = () => listeners.forEach((fn) => fn({ approvers: chain, loaded }));

export function refreshApprovers() {
  pending = fetch(`${API_ORIGIN}/api/direct-orders/approvers`, { credentials: 'include' })
    .then((response) => (response.ok ? response.json() : { approvers: [] }))
    .then((data) => {
      chain = Array.isArray(data.approvers) ? data.approvers : [];
      notify();
      return chain;
    })
    .catch(() => chain)
    .finally(() => {
      pending = null;
      loaded = true;
      notify();
    });
  return pending;
}

/** Loads the chain if it hasn't been yet (one request however many components ask). */
export function ensureApprovers() {
  if (loaded && chain.length) return Promise.resolve(chain);
  return pending || refreshApprovers();
}

export const getApprovers = () => chain;

/** The approver at a level: 1, 2 or 3. */
export const approverAt = (level) => chain.find((a) => a.level === level) || null;

export const approverByEmail = (email) => {
  const normalised = (email || '').toLowerCase();
  return chain.find((a) => a.email === normalised) || null;
};

export const isApproverEmail = (email) => Boolean(approverByEmail(email));

/** "Claudette Debono Farrugia (Senior Manager)", or the name alone when no position is set. */
export const nameWithTitle = (person) => (person ? (person.title ? `${person.name} (${person.title})` : person.name) : '');

export const firstName = (person) => (person?.name || '').split(' ')[0] || '';

/**
 * { approvers, loaded }, kept up to date: re-renders when the chain loads or changes.
 * Pass enabled=false while nobody is signed in (the endpoint needs a login).
 */
export function useApprovers(enabled = true) {
  const [state, setState] = useState({ approvers: chain, loaded });
  useEffect(() => {
    listeners.add(setState);
    setState({ approvers: chain, loaded });
    return () => listeners.delete(setState);
  }, []);
  useEffect(() => {
    if (enabled) ensureApprovers();
  }, [enabled]);
  return state;
}

/** Forget the chain (on logout), so the next user loads their own view. */
export function clearApprovers() {
  chain = [];
  loaded = false;
  notify();
}
